import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dhfApi } from '../../utils/dhfApi';
import {
  Chip, coverageChip, doneChip, healthColor, isEmptyHtml, safeHtml, testsChip, utc,
} from './dhfUi';
import { DocTabs } from './variantsUi';
import '../../styles/DocReview.css';
import '../../styles/Dhf.css';
import '../../styles/DhfVariants.css';

function Grip() {
  return (
    <svg className="dhf-grip" width="12" height="16" viewBox="0 0 12 16" aria-hidden="true">
      <g fill="currentColor">
        <circle cx="3" cy="3" r="1.5" /><circle cx="9" cy="3" r="1.5" />
        <circle cx="3" cy="8" r="1.5" /><circle cx="9" cy="8" r="1.5" />
        <circle cx="3" cy="13" r="1.5" /><circle cx="9" cy="13" r="1.5" />
      </g>
    </svg>
  );
}

function paragraphsOf(nodes, depth = 1, out = []) {
  (nodes || []).forEach((n) => {
    if (n.kind === 'par') {
      out.push({ id: n.id, num: n.num, name: n.name, depth });
      paragraphsOf(n.children, depth + 1, out);
    } else {
      paragraphsOf(n.children, depth, out);
    }
  });
  return out;
}

/**
 * One document, read like a document (mockup screen 2): numbered paragraphs, each item's Key
 * linking to Orcanos, its description, and small status chips (toggle). View only — editing
 * text is Phase 2.
 *
 * Phase 1 writes ONE thing: a POOL item dragged onto a paragraph is placed there in Orcanos,
 * after a confirm, and only reported placed when the paragraph's children read back with it
 * (`POST /api/dhf/{id}/place`). Moving items between paragraphs and reordering are Phase 2.
 * A keyboard path exists for the same action: Place… on the pool item, then pick a paragraph.
 */
export default function DhfDocumentPage() {
  const { setupId, docId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('pool');
  const [showChips, setShowChips] = useState(true);
  const [showDesc, setShowDesc] = useState(true);
  const [pool, setPool] = useState(null);
  const [q, setQ] = useState('');
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const [pending, setPending] = useState(null);       // {item, parent: {id, label} | null}
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [located, setLocated] = useState(null);
  const [nVariants, setNVariants] = useState(null);

  useEffect(() => {
    dhfApi.variants(setupId, docId).then((r) => setNVariants(r.variants.length)).catch(() => setNVariants(null));
  }, [setupId, docId]);

  const load = useCallback(async (reload = false) => {
    try {
      setData(reload ? await dhfApi.reloadDocument(setupId, docId) : await dhfApi.document(setupId, docId));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [setupId, docId]);
  useEffect(() => { load(); }, [load]);

  const levels = data?.levels;
  const loadPool = useCallback(async () => {
    if (!levels) return;
    try {
      const r = await dhfApi.items(setupId, { doc_id: '', levels: levels.join(','), q, page_size: 300 });
      setPool(r);
    } catch (e) {
      setError(e.message);
    }
  }, [setupId, levels, q]);
  useEffect(() => { loadPool(); }, [loadPool]);

  const paragraphs = useMemo(() => paragraphsOf(data?.nodes), [data]);
  const docRoot = data ? { id: data.doc.id, label: `${data.doc.name} (top level)` } : null;

  const place = async () => {
    if (!pending?.parent) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await dhfApi.place(setupId, {
        uid: pending.item.uid, doc_id: docId, parent_id: pending.parent.id, parent_label: pending.parent.label,
      });
      setResult(r.IsSuccess
        ? { tone: 'good', text: `${pending.item.key} is now under ${pending.parent.label}.` }
        : { tone: 'warn', text: r.detail || 'Not placed.' });
      setPending(null);
      await load();
      await loadPool();
    } catch (e) {
      setResult({ tone: 'bad', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const dropProps = (target) => ({
    onDragOver: (e) => { if (dragging) { e.preventDefault(); setOver(target.id); } },
    onDragLeave: () => setOver((o) => (o === target.id ? null : o)),
    onDrop: (e) => {
      e.preventDefault();
      setOver(null);
      if (dragging) setPending({ item: dragging, parent: target });
      setDragging(null);
    },
  });

  const locate = (id) => {
    setLocated(id);
    const el = document.getElementById(`dhf-par-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderNodes = (nodes, depth) => (nodes || []).map((n) => {
    if (n.kind === 'par') {
      const label = `${n.num} ${n.name}`;
      const H = `h${Math.min(depth + 1, 4)}`;
      const cls = `dhf-par dhf-par--${Math.min(depth, 3)}${over === n.id ? ' dhf-par--drop' : ''}`
        + `${located === n.id ? ' dhf-par--located' : ''}`;
      return (
        <div key={n.id}>
          <H id={`dhf-par-${n.id}`} className={cls} {...dropProps({ id: n.id, label })}
             onClick={() => { if (located === n.id) setLocated(null); }}>
            <span className="dhf-par-num">{n.num}</span>{n.name}
          </H>
          {showDesc && !isEmptyHtml(n.description) && (
            <div className="dhf-par-desc" dangerouslySetInnerHTML={{ __html: safeHtml(n.description) }} />
          )}
          {renderNodes(n.children, depth + 1)}
        </div>
      );
    }
    const f = n.fact;
    return (
      <div key={n.id}>
        <div className={`dhf-item${n.kind === 'other' ? ' dhf-item--other' : ''}`}>
          <div className="dhf-item-main">
            {n.url ? <a className="dhf-key" href={n.url} target="_blank" rel="noopener noreferrer">{n.key} ↗</a>
              : <span className="dhf-key">{n.key}</span>}
            <span className="dhf-item-name">{n.name}</span>
            {showDesc && !isEmptyHtml(n.description) && (
              <div className="dhf-item-desc" dangerouslySetInnerHTML={{ __html: safeHtml(n.description) }} />
            )}
          </div>
          {showChips && (
            <div className="dhf-item-side">
              {f ? (
                <>
                  {doneChip(f, n.status)}
                  {coverageChip(f)}
                  {testsChip(f.tests)}
                </>
              ) : (
                <Chip tone="neutral" title="Not one of this panel's items">{n.status || 'not in panel'}</Chip>
              )}
            </div>
          )}
        </div>
        {n.children?.length > 0 && renderNodes(n.children, depth)}
      </div>
    );
  });

  if (error && !data) return <div className="drv-page"><div className="drv-error" role="alert">{error}</div></div>;
  if (!data) return <div className="drv-page"><div className="drv-muted">Loading…</div></div>;
  const h = data.doc.score?.health;

  return (
    <div className="drv-page">
      <div className="dhf-doc-bar">
        <div className="dhf-doc-bar-l">
          <Link to={`/dhf/${setupId}?doc=${docId}`}>← DHF</Link>
          <strong>{data.doc.name}</strong>
          {data.doc.url ? <a className="dhf-key" href={data.doc.url} target="_blank" rel="noopener noreferrer">{data.doc.key} ↗</a> : <span>{data.doc.key}</span>}
          <span className="dhf-chip" style={{ background: '#F4F1FB', color: healthColor(h) }}>health {h ?? '—'}</span>
          <Chip tone="neutral">View only</Chip>
        </div>
        <div className="dhf-doc-bar-r">
          <label><input type="checkbox" checked={showChips} onChange={(e) => setShowChips(e.target.checked)} /> Status chips</label>
          <label><input type="checkbox" checked={showDesc} onChange={(e) => setShowDesc(e.target.checked)} /> Descriptions</label>
          {data.doc.url && (
            <a className="drv-btn" href={data.doc.url} target="_blank" rel="noopener noreferrer"
               title="Open this document in Orcanos (new tab)">Open in Orcanos ↗</a>
          )}
          <button type="button" className="drv-btn" onClick={() => load(true)} title="Read this document's outline from Orcanos again">
            Reload outline
          </button>
          <span className="drv-muted" style={{ fontSize: 12 }}>Built {utc(data.built_at)}</span>
        </div>
      </div>

      <DocTabs setupId={setupId} docId={docId} active="doc" count={nVariants} />

      {error && <div className="drv-error" role="alert">{error}</div>}
      {!data.outline_ok && <div className="drv-error" role="alert">The outline could not be read: {data.error}</div>}
      {data.truncated && <div className="dhf-warn">This document is very large — only part of its outline is shown.</div>}
      {result && <div className={`dhf-result dhf-chip--${result.tone}`} style={{ padding: '8px 12px', borderRadius: 8 }} role="status">{result.text}</div>}

      <div className="dhf-doc-layout">
        <aside className="drv-card dhf-aside">
          <div className="dhf-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'outline'} className={`dhf-tab${tab === 'outline' ? ' dhf-tab--on' : ''}`} onClick={() => setTab('outline')}>Outline</button>
            <button type="button" role="tab" aria-selected={tab === 'pool'} className={`dhf-tab${tab === 'pool' ? ' dhf-tab--on' : ''}`} onClick={() => setTab('pool')}>
              Pool · {pool ? pool.total : data.pool}
            </button>
          </div>
          {tab === 'outline' ? (
            <ul className="dhf-outline-list">
              {paragraphs.map((p) => (
                <li key={p.id} style={{ paddingLeft: (p.depth - 1) * 12 }}>
                  <a href={`#dhf-par-${p.id}`} onClick={(e) => { e.preventDefault(); locate(p.id); }}>{p.num} {p.name}</a>
                </li>
              ))}
              {paragraphs.length === 0 && <li className="drv-muted">No paragraphs.</li>}
            </ul>
          ) : (
            <>
              <p>Items of this document's levels that are in <strong>no document</strong>. Drag one onto a paragraph, or use <em>Place…</em>.</p>
              <input type="search" className="dhf-search" placeholder="Search pool…" aria-label="Search pool"
                     value={q} onChange={(e) => setQ(e.target.value)} />
              <div className="dhf-pool-list">
                {(pool?.rows || []).map((it) => (
                  <div key={it.uid} className={`dhf-pool-item${dragging?.uid === it.uid ? ' dhf-pool-item--drag' : ''}`}
                       draggable onDragStart={(e) => { e.dataTransfer.setData('text/plain', it.uid); setDragging(it); }}
                       onDragEnd={() => { setDragging(null); setOver(null); }}>
                    <Grip />
                    <div>
                      {it.url ? <a className="dhf-key" href={it.url} target="_blank" rel="noopener noreferrer">{it.key}</a> : <b>{it.key}</b>}
                      <div>{it.name}</div>
                      <button type="button" className="dhf-pool-place" onClick={() => setPending({ item: it, parent: null })}>Place…</button>
                    </div>
                  </div>
                ))}
                {pool && pool.rows.length === 0 && <div className="drv-muted" style={{ fontSize: 12 }}>The pool is empty.</div>}
              </div>
            </>
          )}
          <div className="dhf-note">Moving items <em>between</em> paragraphs, reordering and editing text: Phase 2.</div>
        </aside>

        <article className="dhf-paper">
          <div className="dhf-paper-eyebrow">{data.doc.key} · {data.doc.levels.join(' · ')}</div>
          <h1 className={over === docRoot.id ? 'dhf-par--drop' : ''} {...dropProps(docRoot)}>{data.doc.name}</h1>
          <p className="drv-muted">{data.doc.n_items} items in {data.doc.paragraphs} paragraphs</p>
          {renderNodes(data.nodes, 1)}
        </article>
      </div>

      {pending && (
        <div className="dhf-confirm" role="dialog" aria-label="Confirm placement">
          <span>
            Place <strong>{pending.item.key}</strong> under{' '}
            {pending.parent ? <strong>{pending.parent.label}</strong> : (
              <select aria-label="Paragraph" defaultValue=""
                      onChange={(e) => {
                        const p = paragraphs.find((x) => x.id === e.target.value);
                        setPending({ ...pending, parent: p ? { id: p.id, label: `${p.num} ${p.name}` } : null });
                      }}>
                <option value="" disabled>Choose a paragraph…</option>
                {paragraphs.map((p) => <option key={p.id} value={p.id}>{' '.repeat((p.depth - 1) * 3)}{p.num} {p.name}</option>)}
              </select>
            )}
            ? This sets its parent in Orcanos — it leaves the pool and joins {data.doc.key}.
          </span>
          <button type="button" className="drv-btn dhf-confirm-go" disabled={busy || !pending.parent} onClick={place}>
            {busy ? 'Placing…' : 'Place it'}
          </button>
          <button type="button" className="drv-btn" disabled={busy} onClick={() => setPending(null)}>Cancel</button>
        </div>
      )}
    </div>
  );
}
